import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, X, ChevronLeft, ChevronRight, User } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const initials = (name) => (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();

export default function TeamProfile() {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    document.title = "Profil — DROPS TFR";
    window.scrollTo(0, 0);
    axios
      .get(`${BACKEND}/api/team/${id}`)
      .then((res) => setProfile(res.data))
      .catch(() => setNotFound(true));
  }, [id]);

  const gallery = profile?.gallery || [];

  useEffect(() => {
    const onKey = (e) => {
      if (lightbox === null) return;
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((i) => (i + 1) % gallery.length);
      if (e.key === "ArrowLeft") setLightbox((i) => (i - 1 + gallery.length) % gallery.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightbox, gallery]);

  return (
    <div className="min-h-screen bg-[#09090B] text-white">
      <header className="flex items-center justify-between border-b border-white/10 px-6 py-6 lg:px-16">
        <a href="/team" data-testid="profile-back-link" className="flex items-center gap-2 font-tac text-xs tracking-[0.2em] text-white/70 transition-colors hover:text-[#F5B301]">
          <ArrowLeft className="h-4 w-4" /> ZURÜCK ZUM TEAM
        </a>
      </header>

      {notFound ? (
        <div className="py-40 text-center font-tac text-xs tracking-[0.25em] text-white/40" data-testid="profile-not-found">
          PROFIL NICHT GEFUNDEN
        </div>
      ) : profile === null ? (
        <div className="flex justify-center py-40">
          <Loader2 className="h-6 w-6 animate-spin text-[#F5B301]" />
        </div>
      ) : (
        <main data-testid="team-profile" className="px-6 py-16 lg:px-16">
          <div className="grid gap-12 lg:grid-cols-[360px_1fr]">
            <div>
              <div className="aspect-[4/5] overflow-hidden border border-white/10 bg-white/[0.03]">
                {profile.image_path ? (
                  <img src={`${BACKEND}/api/team-image/${profile.image_path}`} alt={profile.name} className="h-full w-full object-cover" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <span className="font-display text-6xl font-black text-white/15">{initials(profile.name) || <User className="h-16 w-16" />}</span>
                  </div>
                )}
              </div>
            </div>
            <div>
              <h1 data-testid="profile-name" className="font-display text-4xl font-black uppercase tracking-tighter sm:text-5xl">{profile.name}</h1>
              <p data-testid="profile-role" className="mt-3 font-tac text-xs uppercase tracking-[0.3em] text-[#F5B301]">{profile.role}</p>
              {profile.bio && (
                <p data-testid="profile-bio" className="mt-8 max-w-2xl font-tac text-sm leading-relaxed text-[#A1A1AA]">{profile.bio}</p>
              )}
            </div>
          </div>

          {gallery.length > 0 && (
            <section data-testid="profile-gallery" className="mt-20 border-t border-white/10 pt-14">
              <p className="font-tac text-xs tracking-[0.3em] text-[#F5B301]">GALERIE</p>
              <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-3">
                {gallery.map((img, i) => (
                  <button
                    key={img.storage_path}
                    type="button"
                    data-testid={`profile-gallery-item-${i}`}
                    onClick={() => setLightbox(i)}
                    className="group relative block overflow-hidden border border-white/10"
                  >
                    <img src={`${BACKEND}/api/member-image/${img.storage_path}`} alt={img.caption || profile.name} loading="lazy" className="aspect-[4/3] w-full object-cover brightness-[0.92] transition-all duration-700 group-hover:scale-105 group-hover:brightness-100" />
                    {img.caption && (
                      <span className="absolute bottom-2 left-2 right-2 text-left font-tac text-[10px] tracking-[0.15em] text-white opacity-0 transition-opacity duration-500 group-hover:opacity-100">{img.caption}</span>
                    )}
                  </button>
                ))}
              </div>
            </section>
          )}
        </main>
      )}

      {lightbox !== null && gallery[lightbox] && (
        <div data-testid="profile-lightbox" className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm" onClick={() => setLightbox(null)}>
          <button data-testid="profile-lightbox-close" onClick={() => setLightbox(null)} className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center border border-white/20 text-white hover:border-[#F5B301] hover:text-[#F5B301]"><X className="h-5 w-5" /></button>
          <button data-testid="profile-lightbox-prev" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i - 1 + gallery.length) % gallery.length); }} className="absolute left-4 flex h-11 w-11 items-center justify-center border border-white/20 text-white hover:border-[#F5B301] hover:text-[#F5B301] sm:left-8"><ChevronLeft className="h-5 w-5" /></button>
          <figure className="max-h-[85vh] max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <img src={`${BACKEND}/api/member-image/${gallery[lightbox].storage_path}`} alt={gallery[lightbox].caption || profile.name} className="max-h-[78vh] w-auto object-contain" />
            <figcaption className="mt-4 text-center font-tac text-[11px] tracking-[0.2em] text-white/60">{gallery[lightbox].caption || profile.name} · {lightbox + 1}/{gallery.length}</figcaption>
          </figure>
          <button data-testid="profile-lightbox-next" onClick={(e) => { e.stopPropagation(); setLightbox((i) => (i + 1) % gallery.length); }} className="absolute right-4 flex h-11 w-11 items-center justify-center border border-white/20 text-white hover:border-[#F5B301] hover:text-[#F5B301] sm:right-8"><ChevronRight className="h-5 w-5" /></button>
        </div>
      )}
    </div>
  );
}
